/**
 * App-wide door sheet (opened by the key chip, the old door / talk routes and the
 * ring toast) and the doorbell-ring watcher: while the app is open in a home with
 * a doorbell, a new ring shows a toast with "Answer", which opens the sheet with
 * talk ready.
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMatch } from 'react-router-dom';
import { Sheet, SkeletonTiles } from '../../components/ui/index.js';
import { useAccess, useEntitiesVersion, useModel } from '../../lib/hooks.js';
import { store } from '../../lib/store.js';
import { toast } from '../../lib/toast.js';
import { haptic } from '../../lib/native.js';
import { Icon } from '../../lib/icons.js';
import { canSeeDoor, closeDoor, doorSetup, doorTarget, noteRing, openDoor, RING_WINDOW_S, useDoorTarget, type DoorTarget } from './state.js';

const DoorSheetBody = lazy(() => import('./DoorSheet.js'));

export function DoorHost(): ReactNode {
  const target = useDoorTarget();
  const [last, setLast] = useState<DoorTarget | null>(target);
  useEffect(() => {
    if (target) setLast(target);
  }, [target]);
  const inHome = useMatch('/h/:home/*')?.params.home;
  const t = target ?? last;
  return (
    <>
      {inHome && <RingWatcher home={inHome} />}
      {t && <DoorSheet key={t.home} target={t} open={target !== null} />}
    </>
  );
}

function DoorSheet({ target, open }: { target: DoorTarget; open: boolean }): ReactNode {
  const model = useModel(target.home);
  return (
    <Sheet open={open} onClose={closeDoor} title="Front door" subtitle={model?.home.name} icon={<Icon name="key" size={18} />} testId="door-sheet">
      <Suspense fallback={<SkeletonTiles count={3} />}>
        <DoorSheetBody home={target.home} talk={target.talk} onClose={closeDoor} />
      </Suspense>
    </Sheet>
  );
}

function RingWatcher({ home }: { home: string }): ReactNode {
  const model = useModel(home);
  const access = useAccess(home);
  const d = useMemo(() => doorSetup(model), [model]);
  const v = useEntitiesVersion(home, d.rings);
  const handled = useRef(new Map<string, number>());
  const visible = canSeeDoor(model, access);
  useEffect(() => {
    const now = Date.now() / 1000;
    for (const id of d.rings) {
      const s = store.entity(home, id);
      if (s?.s !== 'on') continue;
      const key = `${home}|${id}`;
      const prev = handled.current.get(key);
      handled.current.set(key, s.lc);
      noteRing(home, s.lc);
      // First sight of a sensor that was already on when we connected: chip only, no toast.
      if (prev === undefined && now - s.lc > 15) continue;
      if (prev !== undefined && s.lc <= prev) continue;
      if (!visible || now - s.lc > RING_WINDOW_S) continue;
      haptic('warning');
      if (doorTarget()?.home === home) continue; // the sheet is up already: the camera shows "Rang …"
      // Button + visitor sensors of one ring collapse into one toast (toast() drops duplicates).
      toast("Someone's at the front door", { tone: 'info', ttl: 60_000, action: { label: 'Answer', run: () => openDoor(home, 'talk') } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, v, visible]);
  return null;
}
